import { QueueIdEnum } from 'src/app/care/inbox/shared/enums';
import { addTaskStates } from 'src/app/care/task.states';

/**
 * The angularjs states related to the module inbox
 */
inboxStates.$inject = ['$urlRouterProvider', '$stateProvider'];
export function inboxStates($urlRouterProvider, $stateProvider) {
  $urlRouterProvider
    // "/inbox" (ex: backoffice login redirect url)
    // was not needed in old inbox because we had "squash: true" in state for param "queueId", but can't be done with 2 params "queueId" and "channelQueueId"
    .when('/{companyId}/inbox', [
      '$state',
      '$match',
      function ($state, $match) {
        // TODO maybe go to auth.alcmeon.inbox.list.tasks directly, when defaultQueueId is working
        $state.go('auth.alcmeon.after-login', {
          companyId: $match.companyId,
        });
        return true;
      },
    ])

    // "/<companyId>/inbox/task/<id>..."
    .when(/^\/(\d+)\/inbox\/task\/(\d+)/, [
      '$state',
      '$match',
      '$location',
      function ($state, $match, $location) {
        $state.go('auth.alcmeon.inbox.list.tasks.process', {
          companyId: $match[1],
          processedTaskId: $match[2],
          ...$location.search() // pass all query params in $stateParams
        });
        return true;
      },
    ])

    // "/<companyId>/inbox/<siteId>...task/<id>..."
    // TODO (SP-5943) remove after old inbox is totally removed, or maybe not if there is still urls in DB ?!
    .when(/^\/(\d+)\/inbox\/\d+.*task\/(\d+)/, [
      '$state',
      '$match',
      function ($state, $match) {
        $state.go('auth.alcmeon.inbox.list.tasks.process', {
          companyId: $match[1],
          processedTaskId: $match[2],
        });
        return true;
      },
    ])

    // "/<companyId>/inbox/0..." : remove /0 and keep everything after
    // TODO (SP-5943) remove after old inbox is totally removed
    .when(/^\/\d+\/inbox\/0.*/, [
      '$match',
      function ($match) {
        return $match[0].replace('/0', '');
      },
    ])

    // queueId without channelQueueId
    .when('/{companyId}/inbox/{queueId}', [
      '$state',
      '$match',
      function ($state, $match) {
        if ($match.queueId) {
          $state.go('auth.alcmeon.inbox.list.tasks', {
            companyId: $match.companyId,
            queueId: $match.queueId,
          });
        } else {
          // TODO maybe go to auth.alcmeon.inbox.list.tasks directly, when defaultQueueId is working
          $state.go('auth.alcmeon.after-login', {
            companyId: $match.companyId,
          });
        }
        return true;
      },
    ])

    // tempo redirection after enabling new inbox by default (for users with saved urls)
    // TODO (maybe in SP-5943) remove a few months after deploy
    .when(/^\/\d+\/new-inbox.*/, [
      '$match',
      function ($match) {
        return $match[0].replace('new-inbox', 'inbox');
      },
    ])

    // tempo redirection after removing old inbox totally (for users with saved urls)
    // TODO (maybe in SP-5943) remove a few months after deploy
    .when(/^\/\d+\/old-inbox.*/, [
      '$match',
      function ($match) {
        return $match[0].replace('old-inbox', 'inbox');
      },
    ]);

  $stateProvider
    .state('auth.alcmeon.inbox', {
      abstract: true,
      url: '/inbox',
    })
    .state('auth.alcmeon.inbox.list', {
      url: '/{queueId}/{channelQueueId}',
      abstract: true,
      params: {
        queueId: {
          value: QueueIdEnum.Default,
        },
        channelQueueId: {
          value: QueueIdEnum.All,
        },
      },
      resolve: {
        initTaskListConfigs: [
          'AngularNavigationService',
          'initFromSelf',
          async function (AngularNavigationService) {
            // initFromSelf injection above is necessary to wait for the result of /self
            await AngularNavigationService.initSelectedSitesAndAvailableTaskListConfigs();
          },
        ],
      },
    })
    .state('auth.alcmeon.inbox.list.tasks', {
      url: '',
      resolve: {
        taskListConfiguration: [
          'AngularNavigationService',
          '$rootScope',
          '$state',
          '$stateParams',
          'loggedUser',
          'initTaskListConfigs',
          // initTaskListConfigs injection above ensures we are called after InboxNavService has inited the inbox configs
          async function (AngularNavigationService, $rootScope, $state, $stateParams, loggedUser) {
            const { defaultQueueId } = loggedUser; // KO, it's default_queue_id. The use case works because it treated in auth.alcmeon.after-login. TODO update e2e when fixing it.
            const { previousState } = $rootScope;
            if (defaultQueueId && !previousState.name && $stateParams.queueId !== defaultQueueId) {
              $state.go('auth.alcmeon.inbox.list.tasks', { ...$stateParams, queueId: defaultQueueId });
            }

            let selectedTaskListConfig = await AngularNavigationService.getCurrentTaskListConfiguration(
              $stateParams.queueId,
              $stateParams.channelQueueId
            );

            if (!selectedTaskListConfig) {
              // the user entered probably an invalid or old url.
              console.warn(`couldn't find new inbox queue ${$stateParams.queueId}`);

              // and here redirect to auth.alcmeon.inbox.list.tasks via auth.alcmeon.after-login to get correct default_queue_id
              return $state.go('auth.alcmeon.after-login', {
                companyId: $stateParams.companyId,
              });
            }

            return selectedTaskListConfig;
          },
        ],
      },
      views: {
        'container@auth': {
          template: require('src/ajs-app/components/tasks/tasks-list/tasks-list-ng/tasks-list-ng-inbox.html'),
          controller: 'TasksListNgController',
        },
        // these 2 following views are necessary to match the existing page structure, with all herited styles from everywhere, a real mess
        'tabs-nav@auth': {
          controller: [
            '$scope',
            'taskListConfiguration',
            function ($scope, taskListConfiguration) {
              $scope.taskListConfiguration = taskListConfiguration;
            },
          ],
          template:
            '<angular-tabs-navigation [task-list-configuration]="taskListConfiguration"></angular-tabs-navigation>',
        },
        'sub-nav@auth': {
          controller: [
            '$scope',
            'taskListConfiguration',
            function ($scope, taskListConfiguration) {
              $scope.taskListConfiguration = taskListConfiguration;
            },
          ],
          template: `<angular-left-navigation
            [task-list-configuration]="taskListConfiguration"
            class="minimal-scrollbar"></angular-left-navigation>`,
        },
      },
    });

  addTaskStates($stateProvider, 'auth.alcmeon.inbox.list.tasks');
}
